import {
  type ExcelExport,
  ReconciliationConfidence,
  ReconciliationStatus,
  VersionCompareReviewStatus,
  type VersionComparison,
} from '@consigli/types';
import { TFunction } from 'i18next';

const STATUS_LABEL_KEYS: Record<ReconciliationStatus, string> = {
  [ReconciliationStatus.ADDRESSED]: 'version-compare.reconciliation.status.addressed',
  [ReconciliationStatus.UNADDRESSED]: 'version-compare.reconciliation.status.unaddressed',
  [ReconciliationStatus.INDETERMINATE]: 'version-compare.reconciliation.status.indeterminate',
  [ReconciliationStatus.NA]: 'version-compare.reconciliation.status.na',
};

const REVIEW_LABEL_KEYS: Record<VersionCompareReviewStatus, string> = {
  [VersionCompareReviewStatus.UNREVIEWED]: 'version-compare.reconciliation.review.unreviewed',
  [VersionCompareReviewStatus.REVIEWED]: 'version-compare.reconciliation.review.reviewed',
  [VersionCompareReviewStatus.RESOLVED]: 'version-compare.reconciliation.review.resolved',
  [VersionCompareReviewStatus.NOT_RELEVANT]: 'version-compare.reconciliation.review.not-relevant',
};

const CONFIDENCE_LABEL_KEYS: Record<ReconciliationConfidence, string> = {
  [ReconciliationConfidence.HIGH]: 'version-compare.reconciliation.confidence.high',
  [ReconciliationConfidence.MEDIUM]: 'version-compare.reconciliation.confidence.medium',
  [ReconciliationConfidence.LOW]: 'version-compare.reconciliation.confidence.low',
};

const formatVersionCell = (
  content: string | null | undefined,
  blobName: string | null | undefined,
  pageNumber: number | null | undefined,
): string => {
  const lines: string[] = [];
  if (content) lines.push(content);
  if (blobName) {
    lines.push(pageNumber != null ? `${blobName} · P.${pageNumber + 1}` : blobName);
  }
  return lines.join('\r\n');
};

const formatReconciliation = (comparison: VersionComparison, t: TFunction): string => {
  const { reconciliationStatus, reconciliationConfidence } = comparison;
  if (!reconciliationStatus || reconciliationStatus === ReconciliationStatus.NA) return '';
  const status = t(STATUS_LABEL_KEYS[reconciliationStatus]);
  if (!reconciliationConfidence) return status;
  return `${status}\r\n${t(CONFIDENCE_LABEL_KEYS[reconciliationConfidence])}`;
};

export const createVersionCompareExcelExport = (
  comparisons: VersionComparison[],
  t: TFunction,
  title: string,
): ExcelExport => {
  const headers = [
    'ID',
    t('version-compare.reconciliation.change-description-label'),
    t('version-compare.reconciliation.col-version-a'),
    t('version-compare.reconciliation.col-version-b'),
    t('version-compare.reconciliation.col-match-confidence'),
    t('version-compare.reconciliation.col-comment-text'),
    t('version-compare.reconciliation.col-reconciliation'),
    t('version-compare.reconciliation.col-review'),
  ];

  const data = comparisons.map((comparison) => [
    comparison.id,
    comparison.changeDescription,
    formatVersionCell(comparison.content, comparison.blobName, comparison.pageNumber),
    formatVersionCell(comparison.refContent, comparison.refBlobName, comparison.refPageNumber),
    comparison.matchConfidence != null ? `${comparison.matchConfidence}%` : '',
    comparison.commentText ?? '',
    formatReconciliation(comparison, t),
    t(REVIEW_LABEL_KEYS[comparison.reviewStatus]),
  ]);

  return { title, headers, data };
};
